import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';

import type { contractOutput } from '#backend/contract';
import { environment } from '../../../environments/environment';
import { SmartOrpcClientService } from '../../shared/services/smart-orpc-client.service';
import {
  AuthenticatedUser,
  UserService,
} from '../../shared/services/user.service';
import { getAppNow, toZonedDateTime } from '../../shared/utils/date-utils';
import { TypeSafeMatCellDef } from '../../type-safe-mat-cell-def.directive';

type ContractSummary = contractOutput['organization']['contractSummary'];
interface summaryTable {
  product: {
    name: string;
    subTitle?: string;
    unit: string;
  };
  contracted: number | null;
  used: number;
  isHectares: boolean;
}

@Component({
  selector: 'app-contract-summary-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    MatChipsModule,
    MatDividerModule,
    MatToolbarModule,
    MatTabsModule,
    DatePipe,
    TypeSafeMatCellDef,
  ],
  templateUrl: './contract-summary-dashboard.component.html',
  styleUrl: './contract-summary-dashboard.component.scss',
})
export class ContractSummaryDashboardComponent implements OnInit {
  private orpcService = inject(SmartOrpcClientService);
  private userService = inject(UserService);

  contractSummary: ContractSummary | null = null;
  currentUser: AuthenticatedUser | null = null;
  loading = true;
  error: string | null = null;

  dataSource: summaryTable[] = [];
  displayedColumnsWithCommitment: string[] = [
    'product',
    'contracted',
    'used',
    'usagePercentage',
  ];
  displayedColumnsWithoutCommitment: string[] = ['product', 'used'];

  ngOnInit() {
    this.loadContractSummary();
  }

  async loadContractSummary() {
    try {
      this.loading = true;
      this.error = null;

      this.currentUser = this.userService.user();

      let organizationId: number;
      if (environment.skipAuth) {
        organizationId = 1;
      } else if (this.currentUser?.organizationId) {
        organizationId = this.currentUser.organizationId;
      } else {
        this.error = 'Usuário não possui organização associada';
        this.loading = false;
        return;
      }

      const result =
        await this.orpcService.safeClient.organization.contractSummary({
          organizationId,
        });

      if (result.isSuccess && result.data) {
        this.contractSummary = result.data;
        this.dataSource = this.buildDataSource(result.data);
      } else {
        this.error =
          result.error?.message || 'Erro ao carregar resumo do contrato';
      }
    } catch (error) {
      console.error('Erro ao carregar resumo do contrato:', error);
      this.error = 'Erro ao carregar resumo do contrato';
    } finally {
      this.loading = false;
    }
  }

  buildDataSource(contractSummary: ContractSummary) {
    const datasource: summaryTable[] = [];
    datasource.push({
      product: { name: 'Compliance Socioambiental', unit: 'nº relatórios' },
      contracted: contractSummary.complianceReports.contracted,
      used: contractSummary.complianceReports.emitted,
      isHectares: false,
    });
    datasource.push({
      product: { name: 'Histórico de Safras', unit: 'nº relatórios' },
      contracted: contractSummary.historyReports.contracted,
      used: contractSummary.historyReports.emitted,
      isHectares: false,
    });

    datasource.push({
      product: {
        name: 'Monitoramento da Safra',
        unit: 'Hectares contratados',
      },
      contracted: contractSummary.monitoring.contractedHectares,
      used: contractSummary.monitoring.requestedHectares,
      isHectares: true,
    });

    return datasource;
  }

  getProgressPercentage(emitted: number, contracted: number | null): number {
    if (contracted === null || contracted === undefined) {
      return 0;
    }
    return (emitted / contracted) * 100;
  }

  getProgressColor(emitted: number, contracted: number | null): string {
    const percentage = this.getProgressPercentage(emitted, contracted);
    if (percentage >= 100) return 'progress-bar-warning';
    if (percentage >= 80) return 'progress-bar-accent';
    return 'progress-bar-primary';
  }

  getModalityText(modality: string): string {
    return modality === 'with_commitment'
      ? 'Com Compromisso de Consumo'
      : 'Sem Compromisso de Consumo';
  }

  getModalityColor(modality: string): string {
    return modality === 'with_commitment' ? 'primary' : 'accent';
  }

  formatHectares(hectares: number): string {
    return hectares.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  getContractStatus(): { status: string; color: string } {
    if (!this.contractSummary) return { status: '', color: '' };

    const now = getAppNow().epochMilliseconds;
    const startDate = toZonedDateTime(
      this.contractSummary.contractStartDate,
    ).epochMilliseconds;
    const endDate = toZonedDateTime(
      this.contractSummary.contractEndDate,
    ).epochMilliseconds;

    if (now < startDate) {
      return { status: 'Contrato não iniciado', color: 'accent' };
    } else if (now > endDate) {
      return { status: 'Contrato expirado', color: 'warn' };
    } else {
      return { status: 'Contrato ativo', color: 'primary' };
    }
  }
}
